var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as React from "react";
import "./WebcamPreview.component.css";
import { useEffect, useRef, useState } from "react";
import { useMediaDevicesStore } from "../../store/media-devices.store";
import { WebcamBackgroundService, webcamBackgroundService } from "../../../modules/webcam/WebcamBackgroundService";
import { preferencesService } from "../../../modules/preferences/PreferencesService";
import { WarningDisplay } from "../warning-display/WarningDisplay.component";
import { useTranslation } from "react-i18next";
import { configureWebcamBackground, configureWebcamSegmentation, getWebcamBackgroundMode } from "../../../modules/webcam/WebcamBackgroundSettings";
export function WebcamPreview(webcamPreviewProps) {
    const videoRef = useRef(null);
    const [currentStream, setCurrentStream] = useState(null);
    const currentStreamRef = useRef(null);
    const mediaDevices = useMediaDevicesStore();
    const canvasRef = useRef(null);
    const [showBlurredVideo, setShowBlurredVideo] = useState(false);
    const [shouldUseBlur, setShouldUseBlur] = useState(() => getWebcamBackgroundMode() !== "original");
    const [previewBackgroundService] = useState(() => new WebcamBackgroundService());
    const [blurError, setBlurError] = useState(false);
    const [backgroundRevision, setBackgroundRevision] = useState(0);
    const { t } = useTranslation();
    const backgroundService = webcamPreviewProps.overrideDeviceEnabling ? previewBackgroundService : webcamBackgroundService;
    useEffect(() => {
        const updatePreferences = (key) => __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (key === "webcamPersonThreshold" || key === "webcamEdgeSoftness") {
                configureWebcamSegmentation(previewBackgroundService);
                return true;
            }
            if (key === "useWebcamBlur" || key === "webcamBackgroundMode" || key === "webcamBackgroundImageId") {
                setShouldUseBlur(getWebcamBackgroundMode() !== "original");
                setBackgroundRevision((revision) => revision + 1);
                return true;
            }
            if (key === "webcamBlurRadius") {
                previewBackgroundService.setBlurRadius((_a = preferencesService.getNumberPreferredValue(key, 9)) !== null && _a !== void 0 ? _a : 9);
                return true;
            }
            return false;
        });
        preferencesService.onPreferenceChanged.add(updatePreferences);
        return () => {
            preferencesService.onPreferenceChanged.delete(updatePreferences);
        };
    }, [previewBackgroundService]);
    useEffect(() => {
        setShowBlurredVideo(false);
        setBlurError(false);
        if (!webcamPreviewProps.overrideDeviceEnabling || !shouldUseBlur || currentStream === null)
            return;
        let disposed = false;
        previewBackgroundService.onError = () => {
            if (!disposed)
                setBlurError(true);
        };
        previewBackgroundService.setVideoStream(currentStream);
        void configureWebcamBackground(previewBackgroundService, () => !disposed).then(() => __awaiter(this, void 0, void 0, function* () {
            if (!disposed)
                yield previewBackgroundService.start();
        })).catch((error) => {
            if (!disposed) {
                previewBackgroundService.stop();
                console.error("Error starting webcam preview blur:", error);
                setBlurError(true);
            }
        });
        return () => {
            disposed = true;
            previewBackgroundService.onError = undefined;
            previewBackgroundService.stop();
        };
    }, [currentStream, shouldUseBlur, webcamPreviewProps.overrideDeviceEnabling, previewBackgroundService, backgroundRevision]);
    const drawBlurredVideo = () => {
        const hasOutputFrame = shouldUseBlur && backgroundService.hasOutputFrame();
        setShowBlurredVideo(hasOutputFrame);
        if (!hasOutputFrame)
            return;
        if (shouldUseBlur && canvasRef.current) {
            const serviceCanvas = backgroundService.getCanvas();
            if (serviceCanvas && serviceCanvas.width > 0 && serviceCanvas.height > 0) {
                if (canvasRef.current.width !== serviceCanvas.width ||
                    canvasRef.current.height !== serviceCanvas.height) {
                    canvasRef.current.width = serviceCanvas.width;
                    canvasRef.current.height = serviceCanvas.height;
                }
                const ctx = canvasRef.current.getContext("2d");
                ctx === null || ctx === void 0 ? void 0 : ctx.clearRect(0, 0, serviceCanvas.width, serviceCanvas.height);
                ctx === null || ctx === void 0 ? void 0 : ctx.drawImage(serviceCanvas, 0, 0, serviceCanvas.width, serviceCanvas.height);
            }
        }
    };
    useEffect(() => {
        let animationFrameId;
        const render = () => {
            drawBlurredVideo();
            animationFrameId = requestAnimationFrame(render);
        };
        if (shouldUseBlur) {
            render();
        }
        return () => {
            cancelAnimationFrame(animationFrameId);
        };
    }, [shouldUseBlur, currentStream, backgroundService]);
    const stopStream = (stream) => {
        if (stream) {
            stream.getTracks().forEach((track) => {
                track.stop();
            });
        }
    };
    const clearCurrentStream = () => {
        setCurrentStream((current) => {
            if (current) {
                stopStream(current);
            }
            return null;
        });
    };
    const updateStream = () => {
        clearCurrentStream();
        if (mediaDevices.cam.enabled || webcamPreviewProps.overrideDeviceEnabling) {
            window.navigator.mediaDevices
                .getUserMedia({ video: { deviceId: mediaDevices.cam.deviceId, width: 320 }, audio: false })
                .then((stream) => {
                setCurrentStream(stream);
            })
                .catch((err) => {
                console.error(err);
            });
        }
        else {
            setCurrentStream(null);
        }
    };
    useEffect(() => {
        return () => {
            stopStream(currentStreamRef.current);
        };
    }, []);
    useEffect(() => {
        updateStream();
    }, [mediaDevices.cam.enabled, mediaDevices.cam.deviceId]);
    useEffect(() => {
        currentStreamRef.current = currentStream;
        if (videoRef.current && currentStream) {
            if (currentStream) {
                videoRef.current.srcObject = currentStream;
            }
            else {
                videoRef.current.pause();
                videoRef.current.srcObject = null;
            }
        }
    }, [currentStream]);
    return (React.createElement(React.Fragment, null,
        blurError && (React.createElement(WarningDisplay, null, t("settings:warning-webcam-background-failed", "The webcam background could not be applied. Showing the original local preview."))),
        !!currentStream && (React.createElement("div", { className: "webcam-preview-container" },
            React.createElement("video", { ref: videoRef, autoPlay: true, muted: true, playsInline: true, style: shouldUseBlur && showBlurredVideo ? { visibility: "hidden", position: "absolute", zIndex: "-1000" } : undefined }))),
        !!currentStream && shouldUseBlur && (React.createElement("div", { className: "webcam-preview-container-blurred", hidden: !showBlurredVideo },
            React.createElement("canvas", { ref: canvasRef, width: 320, height: 180 })))));
}
